import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Truck, Zap, Calendar, Package, ArrowRight, CheckCircle } from 'lucide-react';
import { mockServices, contactInfo } from '../mock';
import { useNavigate } from 'react-router-dom';
import Tilt from 'react-parallax-tilt';
import { useInView } from 'react-intersection-observer';

const iconMap = {
  Truck: Truck,
  Zap: Zap,
  Calendar: Calendar,
  Package: Package
};

export const Services = () => {
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="services" className="py-20 bg-gradient-to-br from-blue-50 via-white to-purple-50 relative overflow-hidden">
      {/* Animated Background Orbs */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl animate-float-delayed" />
      </div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" ref={ref}>
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium mb-6">
            <Package className="h-4 w-4 mr-2" />
            <span>Our Services</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Comprehensive Delivery Solutions
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            From urgent same-day deliveries to scheduled bulk shipments, we provide reliable solutions 
            tailored to your business needs across the entire UK.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {mockServices.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const colorSchemes = [
              'bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-400',
              'bg-gradient-to-br from-blue-50 to-white border-l-4 border-blue-400', 
              'bg-gradient-to-br from-purple-50 to-white border-l-4 border-purple-400',
              'bg-gradient-to-br from-orange-50 to-white border-l-4 border-orange-400'
            ];
            
            return (
              <Tilt
                key={service.id}
                className={`transform transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
                tiltMaxAngleX={8}
                tiltMaxAngleY={8}
                perspective={1000}
                scale={1.02}
                transitionSpeed={2000}
                glareEnable={true}
                glareMaxOpacity={0.15}
                glareColor="#10b981"
                glarePosition="all"
                glareBorderRadius="16px"
              >
                <Card 
                  className={`relative p-8 border-0 shadow-xl hover:shadow-2xl transition-all duration-500 group overflow-hidden ${colorSchemes[index % 4]}`}
                  style={{
                    transform: 'translateZ(50px)',
                    transformStyle: 'preserve-3d',
                  }}
                >
                  {/* Animated Gradient Border */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-30 transition-opacity duration-700 animate-shimmer" />
                  
                  {/* Background Pattern */}
                  <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform rotate-12 translate-x-8 -translate-y-8">
                    <IconComponent className="w-full h-full" />
                  </div>
                  
                  <CardContent className="p-0 relative z-10">
                    {/* Service Icon with 3D depth */}
                    <div 
                      className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 ${
                        index % 4 === 0 ? 'bg-emerald-100 text-emerald-600' :
                        index % 4 === 1 ? 'bg-blue-100 text-blue-600' :
                        index % 4 === 2 ? 'bg-purple-100 text-purple-600' :
                        'bg-orange-100 text-orange-600'
                      } group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg`}
                      style={{ transform: 'translateZ(60px)' }}
                    >
                      <IconComponent className="h-8 w-8" />
                    </div>

                    {/* Service Title */}
                    <h3 
                      className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-emerald-600 transition-colors duration-300"
                      style={{ transform: 'translateZ(40px)' }}
                    >
                      {service.title}
                    </h3>

                    {/* Service Description */}
                    <p 
                      className="text-slate-600 text-lg mb-6 leading-relaxed"
                      style={{ transform: 'translateZ(30px)' }}
                    >
                      {service.description}
                    </p>

                    {/* Service Features */}
                    <div className="space-y-3 mb-8" style={{ transform: 'translateZ(20px)' }}>
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center space-x-3">
                          <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                          <span className="text-slate-700 font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Get Quote Button */}
                    <Button 
                      className={`w-full ${
                        index % 4 === 0 ? 'bg-emerald-600 hover:bg-emerald-700' :
                        index % 4 === 1 ? 'bg-blue-600 hover:bg-blue-700' :
                        index % 4 === 2 ? 'bg-purple-600 hover:bg-purple-700' :
                        'bg-orange-600 hover:bg-orange-700'
                      } text-white py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2`}
                      style={{ transform: 'translateZ(70px)' }}
                      onClick={() => {
                        navigate('/book');
                        setTimeout(() => {
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }, 100);
                      }}
                    >
                      <span>Get Quote</span>
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </Button>
                  </CardContent>
                </Card>
              </Tilt>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-slate-50 to-emerald-50 rounded-2xl p-12 border border-emerald-100">
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            Ready to Get Started?
          </h3>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Call us now for an instant quote tailored to your delivery needs. 
            Our team is standing by to help you get your products delivered fast and reliably.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center space-x-2"
              onClick={() => window.open(`tel:${contactInfo.phone}`, '_self')}
            >
              <span>Call {contactInfo.phone}</span>
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-8 py-4 text-lg font-semibold rounded-xl transition-all duration-300 flex items-center space-x-2"
              onClick={() => navigate('/book')}
            >
              <span>Contact Us</span>
              <ArrowRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Custom Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(40px, -60px) scale(1.1); }
          66% { transform: translate(-30px, 30px) scale(0.9); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-float {
          animation: float 20s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: float 20s ease-in-out infinite;
          animation-delay: 10s;
        }
        
        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};